import type { Formatters } from '@resource-kit/core';
import type { TranslateFn } from '@resource-kit/model';
import { jq } from '@resource-kit/controllers';
import type { EntityField } from '@resource-kit/controllers/react';
import { resolveDisplayWidget } from './widgets';
import { FIELD_TYPE_REGISTRY, stringifyRaw, toWidgetField } from './field-type-registry';

/**
 * The host capabilities the text projection needs, as ONE object (#2169 PR-2).
 *
 * Both members come off `useResourceKit()` at the call site, so the parameter
 * is the seam's own shape rather than a pair of positional arguments a caller
 * could transpose. It is `t` and `format` and nothing else: the projection
 * translates labels and renders dates, and widening it to the whole services
 * object would let a projection reach for storage or a confirm dialog.
 *
 * `t` is the NARROW `TranslateFn` rather than the seam's `Translate`, because
 * that is all this chain needs — a key and a `defaultValue` — and the seam's
 * wider function is assignable to it. Declaring the wide one instead would have
 * rejected every caller that already holds a `TranslateFn`.
 */
export interface ExportContext {
  t: TranslateFn;
  format: Formatters;
}

/**
 * Apply a field's `transform` (function or jq-string expression) to a raw cell
 * value, returning the transformed value. Consumed by the CSV text projection
 * (`formatFieldTextForExport`), so derived columns export their derived value
 * (#1288).
 */
function applyFieldTransform<T>(field: EntityField<T>, rawValue: unknown, row: T): unknown {
  if (typeof field.transform === 'function') {
    return field.transform(rawValue, row);
  }
  if (typeof field.transform === 'string') {
    // String transform is a jq-style path/pipe expression applied to the raw
    // cell value. `jq` traverses the value it is given, so passing the cell
    // value directly lets paths like `[].title` map over an array column.
    return jq(rawValue, field.transform);
  }
  return rawValue;
}

/**
 * Plain-text rendering of a field value for CSV export (#1288).
 *
 * The export reads the same as what the operator sees: dates via the host's
 * `formatDate`, currency via the host's `formatCurrency` (#2184 — the same
 * `Formatters` seam member the cell calls, so the two cannot pick different
 * locales), enum values via their translated label, booleans as yes/no. The
 * field's `transform` runs first — identically to the cell — so derived columns
 * export their derived value. A field with a bespoke `render` (arbitrary React
 * node — no reliable text projection) degrades to the raw value string.
 *
 * Since #1805 slice S5 the projection is chosen the same way the CELL chooses
 * its renderer: the field's resolved widget first, the field type second. That
 * is what makes `Widget.csvText` — manifest §2's landing spot for the CSV half
 * of `fields[].render` / `transform` / `copyable` — a real carrier instead of a
 * declared-but-never-read member. Before it, a resource migrated to a named
 * widget would have kept exporting through its raw field TYPE, silently
 * dropping whatever the widget shows on screen.
 *
 * `ctx` is injected (rather than reaching for a global i18n singleton or the
 * host's date helpers) so the export runs in the operator's active language and
 * the merchant's timezone while the function stays unit-testable — and so this
 * module names no host.
 */
export function formatFieldTextForExport<T>(
  field: EntityField<T>,
  rawValue: unknown,
  row: T,
  { t, format }: ExportContext,
): string {
  // 1. Apply the same transform the cell applies.
  const value: unknown = applyFieldTransform(field, rawValue, row);

  // 2. LEGACY carrier with no v2 counterpart (`ModelField` declares none): a
  // custom renderer produces arbitrary JSX with no text projection, so it
  // degrades to the raw value. It stays AHEAD of the widget on purpose — a
  // field declaring both is mid-migration, and the thing actually on screen
  // is still the bespoke renderer.
  if (field.render) return stringifyRaw(value);

  // 3. The field's resolved widget. Resolved through `resolveDisplayWidget`,
  // i.e. against `renderType ?? type`, so the file and the screen cannot
  // disagree about which projection a column uses. `resolveWidget` already
  // backfills an omitted `csvText` from the type default, and the field-type
  // widgets deliberately declare none — so today this is always undefined and
  // the export falls through to step 4 unchanged.
  const widgetCsvText = resolveDisplayWidget(field).csvText;
  if (widgetCsvText) {
    return widgetCsvText({ value, record: row, field: toWidgetField(field), translate: t });
  }

  // 4. The field type's own projection.
  const effectiveType = field.renderType || field.type;
  return FIELD_TYPE_REGISTRY[effectiveType].csvText({ value, field, translate: t, format });
}
